import { useEffect, useState } from "react";
import { Wallet, Lock, ArrowDownToLine, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api, { fmtErr, inr, fmtDateTime } from "../../lib/api";
import { FullScreenLoader } from "../../context/AuthContext";

const W_STATUS = {
  REQUESTED: "bg-amber-100 text-amber-700", UNDER_REVIEW: "bg-amber-100 text-amber-700",
  APPROVED: "bg-blue-100 text-blue-700", PROCESSING: "bg-blue-100 text-blue-700",
  PAID: "bg-emerald-100 text-emerald-700", REJECTED: "bg-red-100 text-red-600", FAILED: "bg-red-100 text-red-600",
};

export default function FranchiseWallet() {
  const [data, setData] = useState(null);
  const [withdrawals, setWithdrawals] = useState([]);
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/franchise/wallet").then(({ data }) => setData(data)).catch((e) => { toast.error(fmtErr(e)); setData({}); });
    api.get("/franchise/withdrawals").then(({ data }) => setWithdrawals(data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const requestWithdrawal = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/franchise/withdrawals", { amount: parseFloat(amount) });
      toast.success("Withdrawal requested — amount locked until processed");
      setAmount("");
      load();
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setBusy(false);
    }
  };

  if (!data) return <FullScreenLoader label="Loading wallet" />;
  const w = data.wallet || {};

  return (
    <div className="space-y-5" data-testid="franchise-wallet-page">
      <div>
        <h1 className="font-display text-2xl font-bold text-shipany-navy">Earnings Wallet</h1>
        <p className="text-sm text-slate-500">Your commission earnings. Minimum withdrawal {inr(data.min_withdrawal || 500)}.</p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="wallet-available-card">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase text-slate-400"><Wallet className="h-4 w-4 text-emerald-500" /> Available</div>
          <p className="mono mt-2 text-2xl font-bold text-emerald-600">{inr(w.available ?? 0)}</p>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="wallet-locked-card">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase text-slate-400"><Lock className="h-4 w-4 text-amber-500" /> Locked (payouts)</div>
          <p className="mono mt-2 text-2xl font-bold text-amber-600">{inr(w.locked ?? 0)}</p>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="wallet-withdrawn-card">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase text-slate-400"><ArrowDownToLine className="h-4 w-4 text-shipany-blue" /> Total Withdrawn</div>
          <p className="mono mt-2 text-2xl font-bold text-shipany-navy">{inr(w.total_withdrawn ?? 0)}</p>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="wallet-recoverable-card">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase text-slate-400"><AlertTriangle className="h-4 w-4 text-red-400" /> Recoverable</div>
          <p className="mono mt-2 text-2xl font-bold text-red-500">{inr(w.recoverable ?? 0)}</p>
        </div>
      </div>

      <div className="grid gap-5 lg:grid-cols-3">
        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="withdrawal-request-card">
          <h2 className="text-sm font-bold uppercase tracking-wide text-slate-500">Request Withdrawal</h2>
          <form onSubmit={requestWithdrawal} className="mt-4 space-y-3">
            <div>
              <label className="mb-1 block text-xs font-semibold text-slate-500">Amount (min {inr(data.min_withdrawal || 500)})</label>
              <input type="number" min={data.min_withdrawal || 500} step="0.01" required value={amount} onChange={(e) => setAmount(e.target.value)}
                data-testid="withdrawal-amount-input"
                className="w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-sm focus:border-shipany-blue focus:outline-none focus:ring-2 focus:ring-shipany-blue/15" />
            </div>
            <button disabled={busy} data-testid="withdrawal-submit-btn" className="w-full rounded-xl bg-shipany-blue py-2.5 text-sm font-bold text-white disabled:opacity-60">
              {busy ? "Requesting…" : "Request Withdrawal"}
            </button>
            <p className="text-[11px] text-slate-400">Amount moves from available to locked instantly. Paid manually by ShipAny finance to your verified bank account.</p>
          </form>

          <h3 className="mt-6 text-xs font-bold uppercase tracking-wide text-slate-400">Withdrawal History</h3>
          <div className="mt-2 space-y-2">
            {withdrawals.map((x) => (
              <div key={x.withdrawal_id} className="flex items-center justify-between rounded-xl border border-slate-100 px-3.5 py-2.5" data-testid={`withdrawal-row-${x.withdrawal_id}`}>
                <div><p className="mono text-sm font-bold text-shipany-navy">{inr(x.amount)}</p><p className="text-[10px] text-slate-400">{fmtDateTime(x.requested_at)}{x.payment_reference ? ` · Ref ${x.payment_reference}` : ""}</p></div>
                <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${W_STATUS[x.status] || "bg-slate-100 text-slate-500"}`} data-testid={`withdrawal-status-${x.withdrawal_id}`}>{x.status}</span>
              </div>
            ))}
            {!withdrawals.length && <p className="py-3 text-center text-xs text-slate-400" data-testid="withdrawals-empty">No withdrawals yet.</p>}
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm lg:col-span-2" data-testid="wallet-ledger-card">
          <h2 className="text-sm font-bold uppercase tracking-wide text-slate-500">Ledger</h2>
          <div className="mt-3 overflow-x-auto">
            <table className="w-full min-w-[620px] text-sm">
              <thead><tr className="border-b border-slate-100 text-left text-xs font-bold uppercase text-slate-400"><th className="py-2 pr-4">Date</th><th className="py-2 pr-4">Type</th><th className="py-2 pr-4 text-right">Credit</th><th className="py-2 pr-4 text-right">Debit</th><th className="py-2 pr-4 text-right">Balance</th><th className="py-2">Remarks</th></tr></thead>
              <tbody>
                {(data.ledger || []).map((l) => (
                  <tr key={l.entry_id} className="border-b border-slate-50" data-testid={`ledger-row-${l.entry_id}`}>
                    <td className="py-2.5 pr-4 text-xs text-slate-500">{fmtDateTime(l.created_at)}</td>
                    <td className="py-2.5 pr-4 text-xs font-semibold capitalize">{String(l.transaction_type).replace(/_/g, " ")}</td>
                    <td className="mono py-2.5 pr-4 text-right text-xs font-bold text-emerald-600">{l.credit ? inr(l.credit) : "—"}</td>
                    <td className="mono py-2.5 pr-4 text-right text-xs font-bold text-red-500">{l.debit ? inr(l.debit) : "—"}</td>
                    <td className="mono py-2.5 pr-4 text-right text-xs font-semibold">{inr(l.new_balance)}</td>
                    <td className="py-2.5 text-[11px] text-slate-500">{l.remarks}</td>
                  </tr>
                ))}
                {!(data.ledger || []).length && <tr><td colSpan={6} className="py-6 text-center text-xs text-slate-400" data-testid="ledger-empty">No ledger entries yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
